import { useState } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MessageSquare, Bug, Lightbulb, MessageCircle, CheckCircle2, X, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const categories = [
  { id: "bug", label: "Bug Report", icon: Bug, color: "text-red-500", bgColor: "bg-red-500/10", borderColor: "border-red-500/30" },
  { id: "feature", label: "Feature Request", icon: Lightbulb, color: "text-blue-500", bgColor: "bg-blue-500/10", borderColor: "border-blue-500/30" },
  { id: "general", label: "General Feedback", icon: MessageCircle, color: "text-violet-500", bgColor: "bg-violet-500/10", borderColor: "border-violet-500/30" },
];

export default function FeedbackModal({ open, onClose }) {
  const { user, token } = useAuth();
  const [category, setCategory] = useState(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const maxChars = 500;
  const charCount = message.length;
  const isValid = category && message.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid) return;
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/feedback`,
        { category, message: message.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitted(true);
      toast.success("Feedback submitted successfully!");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      toast.error(error.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setCategory(null);
    setMessage("");
    setSubmitted(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="feedback-modal">
        {submitted ? (
          // Success State
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Thank You!</h3>
            <p className="text-slate-400 mb-6">
              Your feedback has been submitted successfully. We appreciate you helping us improve FlowGuard.
            </p>
            <Button onClick={handleClose} className="bg-emerald-500 hover:bg-emerald-600 text-white">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-amber-500" />
                Send Feedback
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Help us improve FlowGuard
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {/* Category Selection */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  What type of feedback?
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {categories.map((cat) => {
                    const Icon = cat.icon;
                    const isSelected = category === cat.id;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setCategory(cat.id)}
                        className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                          isSelected 
                            ? `${cat.bgColor} ${cat.borderColor} ${cat.color}` 
                            : "bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600"
                        }`}
                        data-testid={`feedback-category-${cat.id}`}
                      >
                        <Icon className="h-5 w-5" />
                        <span className="text-xs font-medium">{cat.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message Input */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Your Message
                </label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, maxChars))}
                  placeholder="Tell us what's on your mind..."
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-500 min-h-[120px] resize-none"
                  data-testid="feedback-message-input"
                />
                <div className="flex justify-between items-center mt-1">
                  <span className={`text-xs ${charCount > maxChars * 0.9 ? 'text-amber-500' : 'text-slate-500'}`}>
                    {charCount}/{maxChars} characters
                  </span>
                </div>
              </div>

              {/* User Info Note */}
              <p className="text-xs text-slate-500">
                Your feedback will be sent from <span className="text-slate-400">{user?.email}</span>
              </p>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={!isValid || submitting}
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="feedback-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Feedback"
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
